
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MessageSquare, Mail, Send, Users, Bell, Bot, FileText } from 'lucide-react';
import { useToast } from "@/hooks/use-toast";
import { useNotifications } from '@/hooks/useNotifications';
import { useNewsletterSubscribers } from '@/hooks/useNewsletterSubscribers';
import { SubscribersSection } from './SubscribersSection';
import { TelegramBotDialog } from './TelegramBotDialog';
import { SendTelegramMessageDialog } from './SendTelegramMessageDialog';
import { TelegramTestDialog } from './TelegramTestDialog';

export const CommunicationSection = () => {
  const [emailData, setEmailData] = useState({
    recipient_type: '',
    subject: '',
    content: '',
    recipient_emails: ''
  });
  const [notificationData, setNotificationData] = useState({
    title: '',
    message: '',
    recipient_type: '',
    expires_at: '',
    user_id: ''
  });
  const [newsletterData, setNewsletterData] = useState({
    subject: '',
    content: '',
    selectedEmails: [] as string[]
  });
  const [sending, setSending] = useState(false);
  const [telegramBotDialogOpen, setTelegramBotDialogOpen] = useState(false);
  const [telegramMessageDialogOpen, setTelegramMessageDialogOpen] = useState(false);
  const { toast } = useToast();
  const { sendNotification } = useNotifications();
  const { subscribers, sendNewsletterEmail } = useNewsletterSubscribers();

  const handleSendEmail = async () => {
    try {
      setSending(true);
      
      // Simulate email sending
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast({
        title: "Հաջողություն",
        description: "Էլ. նամակները հաջողությամբ ուղարկվեցին"
      });
      
      setEmailData({
        recipient_type: '',
        subject: '',
        content: '',
        recipient_emails: ''
      });
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: "Սխալ",
        description: "Էլ. նամակ ուղարկելիս տեղի ունեցավ սխալ",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendNotification = async () => {
    try {
      setSending(true);
      
      const result = await sendNotification(
        notificationData.recipient_type,
        notificationData.title,
        notificationData.message,
        notificationData.expires_at || undefined,
        notificationData.user_id || undefined
      );

      if (result.success) {
        toast({
          title: "Հաջողություն",
          description: "Ծանուցումները հաջողությամբ ուղարկվեցին"
        });
        
        setNotificationData({
          title: '',
          message: '',
          recipient_type: '',
          expires_at: '',
          user_id: ''
        });
      } else {
        throw new Error('Failed to send notification');
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      toast({
        title: "Սխալ",
        description: "Ծանուցում ուղարկելիս տեղի ունեցավ սխալ",
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  const handleSendNewsletter = async () => {
    if (!newsletterData.subject.trim() || !newsletterData.content.trim()) {
      toast({
        title: "Սխալ",
        description: "Խնդրում ենք մուտքագրել թեմանը և բովանդակությունը",
        variant: "destructive"
      });
      return;
    }

    const success = await sendNewsletterEmail(
      newsletterData.subject, 
      newsletterData.content, 
      newsletterData.selectedEmails.length > 0 ? newsletterData.selectedEmails : undefined
    );
    
    if (success) {
      setNewsletterData({ subject: '', content: '', selectedEmails: [] });
    }
  };

  return (
    <div className="space-y-6">
      {/* Newsletter Subscribers Section */}
      <SubscribersSection 
        subscribers={subscribers} 
        onSendToSelected={(selectedEmails) => {
          // Set selected emails for newsletter sending
          setNewsletterData(prev => ({ 
            ...prev, 
            selectedEmails: selectedEmails 
          }));
        }} 
      />

      {/* Newsletter Email Section */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Newsletter ուղարկում բաժանորդներին
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-muted-foreground">Թեմա</Label>
            <Input
              value={newsletterData.subject}
              onChange={(e) => setNewsletterData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Մուտքագրեք նամակի թեմանը"
              className="bg-muted border-border/70 text-foreground"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-muted-foreground">Հաղորդագրություն</Label>
            <Textarea
              value={newsletterData.content}
              onChange={(e) => setNewsletterData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Մուտքագրեք հաղորդագրության բովանդակությունը"
              rows={6}
              className="bg-muted border-border/70 text-foreground resize-none"
            />
          </div>
          
          <div className="space-y-2">
            {newsletterData.selectedEmails.length > 0 && (
              <div className="text-sm text-muted-foreground">
                Ընտրված բաժանորդներ: {newsletterData.selectedEmails.length}
              </div>
            )}
            <Button 
              onClick={handleSendNewsletter}
              disabled={sending}
              className="h-auto min-h-10 w-full whitespace-normal bg-primary text-sm hover:bg-primary/90"
            >
              <Send className="h-4 w-4 mr-2" />
              {sending ? "Ուղարկվում է..." : 
                newsletterData.selectedEmails.length > 0 
                  ? `Ուղարկել ընտրված բաժանորդներին (${newsletterData.selectedEmails.length})`
                  : `Ուղարկել բոլոր բաժանորդներին (${subscribers.filter(s => s.is_active).length})`
              }
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Email Composer */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Էլ. նամակ ուղարկել
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Ստացողների տեսակ</Label>
              <Select
                value={emailData.recipient_type}
                onValueChange={(value) => setEmailData({ ...emailData, recipient_type: value })}
              >
                <SelectTrigger className="bg-muted border-border/70 text-foreground">
                  <SelectValue placeholder="Ընտրել ստացողների տեսակը" />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border/70">
                  <SelectItem value="all_students">Բոլոր ուսանողները</SelectItem>
                  <SelectItem value="all_lecturers">Բոլոր դասախոսները</SelectItem>
                  <SelectItem value="subscribers">Newsletter բաժանորդները</SelectItem>
                  <SelectItem value="pending_registrations">Սպասող գրանցումները</SelectItem>
                  <SelectItem value="custom">Հատուկ ցանկ</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Վերնագիր</Label>
              <Input
                value={emailData.subject}
                onChange={(e) => setEmailData({ ...emailData, subject: e.target.value })}
                className="bg-muted border-border/70 text-foreground"
                placeholder="Նամակի վերնագիր..."
              />
            </div>
          </div>

          {emailData.recipient_type === 'custom' && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Էլ. փոստերի ցանկ (ստորակետով բաժանված)</Label>
              <Textarea
                value={emailData.recipient_emails}
                onChange={(e) => setEmailData({ ...emailData, recipient_emails: e.target.value })}
                className="bg-muted border-border/70 text-foreground"
                placeholder="email1@example.com; email2@example.com"
                rows={3}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-muted-foreground">Նամակի բովանդակություն</Label>
            <Textarea
              value={emailData.content}
              onChange={(e) => setEmailData({ ...emailData, content: e.target.value })}
              className="bg-muted border-border/70 text-foreground"
              placeholder="Նամակի բովանդակությունը..."
              rows={6}
            />
          </div>

          <Button
            onClick={handleSendEmail}
            disabled={sending || !emailData.recipient_type || !emailData.subject || !emailData.content}
            className="w-full bg-primary hover:bg-primary/90"
          >
            <Send className="h-4 w-4 mr-2" />
            {sending ? "Ուղարկվում է..." : "Ուղարկել նամակ"}
          </Button>
        </CardContent>
      </Card>

      {/* Push Notifications */}
      <Card className="border-border/70 bg-card/80">
        <CardHeader>
          <CardTitle className="text-muted-foreground flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Push ծանուցումներ
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-muted-foreground">Վերնագիր</Label>
              <Input
                value={notificationData.title}
                onChange={(e) => setNotificationData({ ...notificationData, title: e.target.value })}
                className="bg-muted border-border/70 text-foreground"
                placeholder="Ծանուցման վերնագիր..."
              />
            </div>

            <div className="space-y-2">
              <Label className="text-muted-foreground">Ստացողների տեսակ</Label>
              <Select
                value={notificationData.recipient_type}
                onValueChange={(value) => setNotificationData({ ...notificationData, recipient_type: value })}
              >
                <SelectTrigger className="bg-muted border-border/70 text-foreground">
                  <SelectValue placeholder="Ընտրել ստացողները" />
                </SelectTrigger>
                <SelectContent className="bg-muted border-border/70">
                  <SelectItem value="all_users">Բոլոր օգտատերերը</SelectItem>
                  <SelectItem value="students">Ուսանողները</SelectItem>
                  <SelectItem value="lecturers">Դասախոսները</SelectItem>
                  <SelectItem value="admins">Ադմիններ</SelectItem>
                  <SelectItem value="user_id">Անհատական (User ID)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {notificationData.recipient_type === 'user_id' && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">User ID</Label>
              <Input
                value={notificationData.user_id}
                onChange={(e) => setNotificationData({ ...notificationData, user_id: e.target.value })}
                className="bg-muted border-border/70 text-foreground"
                placeholder="Մուտքագրել օգտատիրոջ ID-ն..."
              />
            </div>
          )}

          <div className="space-y-2">
            <Label className="text-muted-foreground">Հաղորդագրություն</Label>
            <Textarea
              value={notificationData.message}
              onChange={(e) => setNotificationData({ ...notificationData, message: e.target.value })}
              className="bg-muted border-border/70 text-foreground"
              placeholder="Ծանուցման բովանդակությունը..."
              rows={4}
            />
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground">Ավարտի ամսաթիվ (ոչ պարտադիր)</Label>
            <Input
              type="datetime-local"
              value={notificationData.expires_at}
              onChange={(e) => setNotificationData({ ...notificationData, expires_at: e.target.value })}
              className="bg-muted border-border/70 text-foreground"
            />
          </div>

          <Button
            onClick={handleSendNotification}
            disabled={sending || !notificationData.title || !notificationData.message || !notificationData.recipient_type || (notificationData.recipient_type === 'user_id' && !notificationData.user_id)}
            className="w-full bg-primary hover:bg-primary/90"
          >
            <Bell className="h-4 w-4 mr-2" />
            {sending ? "Ուղարկվում է..." : "Ուղարկել ծանուցում"}
          </Button>
        </CardContent>
      </Card>

      {/* Communication Tools */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 sm:gap-6">
        {/* Telegram Bot Management */}
        <Card className="border-border/70 bg-card/80">
          <CardHeader>
            <CardTitle className="text-muted-foreground flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Telegram Bot
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="py-6 text-center">
              <Bot className="mx-auto mb-4 h-12 w-12 text-primary" />
              <p className="mb-4 text-sm text-muted-foreground">
                Telegram bot-ի կարգավորումներ և կառավարում
              </p>
              <div className="space-y-2">
                 <Button
                   variant="outline"
                   className="w-full text-primary border-border/70 hover:bg-primary/10"
                   onClick={() => setTelegramBotDialogOpen(true)}
                 >
                   Bot-ի կարգավորումներ
                 </Button>
                  <Button
                    variant="outline"
                    className="w-full text-primary border-border/70 hover:bg-primary/10"
                    onClick={() => setTelegramMessageDialogOpen(true)}
                  >
                    Ուղարկել հաղորդագրություն
                  </Button>
                  <TelegramTestDialog />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Group Communication */}
        <Card className="border-border/70 bg-card/80">
          <CardHeader>
            <CardTitle className="text-muted-foreground flex items-center gap-2">
              <Users className="h-5 w-5" />
              Test Telegram Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="py-6 text-center">
              <MessageSquare className="mx-auto mb-4 h-12 w-12 text-primary" />
              <p className="mb-4 text-sm text-muted-foreground">
                Test անել registration Telegram հաղորդագրությունը
              </p>
              <div className="space-y-2">
                <Button
                  variant="outline"
                  className="h-auto min-h-10 w-full whitespace-normal text-sm text-primary border-border/70 hover:bg-primary/10"
                  onClick={async () => {
                    try {
                      const testData = {
                        firstName: 'Test',
                        lastName: 'User',
                        email: 'test@example.com',
                        phone: '+37498000000',
                        course: 'cybersecurity',
                        faculty: '',
                        specialization: '',
                        language: '',
                        learningType: 'group' as const,
                        learningFormat: 'inPerson' as const,
                        duration: '6'
                      };
                      
                      console.log('🧪 Testing Telegram integration...');
                      const { sendToTelegram } = await import('@/utils/telegramService');
                      const result = await sendToTelegram(testData);
                      
                      toast({
                        title: "Test հաջողություն",
                        description: `Telegram հաղորդագրությունը ուղարկվեց: ${result?.summary || 'Success'}`,
                      });
                    } catch (error: any) {
                      console.error('Test failed:', error);
                      toast({
                        title: "Test սխալ",
                        description: `Telegram integration չի աշխատում: ${error.message}`,
                        variant: "destructive"
                      });
                    }
                  }}
                >
                  Test Registration Message
                </Button>
                <Button
                  variant="outline"
                  className="w-full text-primary border-border/70 hover:bg-primary/10"
                  onClick={() => setTelegramMessageDialogOpen(true)}
                >
                  Manual Telegram Message
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Telegram Bot Settings Dialog */}
      <TelegramBotDialog 
        open={telegramBotDialogOpen} 
        onOpenChange={setTelegramBotDialogOpen} 
      />

      {/* Send Telegram Message Dialog */}
      <SendTelegramMessageDialog 
        open={telegramMessageDialogOpen} 
        onOpenChange={setTelegramMessageDialogOpen} 
      />
    </div>
  );
};
